import React from 'react';
import Div from '../Element/Div/Div';
import Flex from '../Flex/Flex';
import Gutter from '../Gutter/Gutter';
import SmallText from '../Typography/SmallText/SmallText';
import Typography, { SupportedFont } from '../Typography/Typography';

import styles from './Price.module.scss';
import { ArrayElementType } from '../../types/utils/ArrayElementType';
import { CreateProps } from '../../types/utils/CreateProps';

type PriceProps = CreateProps<
  {
    /** Alignment of price texts */
    align?: 'left' | 'right' | 'center';
    /** Prefix for the price, e.g. 'starting from' */
    prefix?: string;
    /** Value for the price */
    value: string | number;
    /** Currency, by default it's € */
    unit?: string;
    /** Helps to the price more precisely per unit or per given time. Separates the currency and specifier with `/`. For example €/month, €/GB */
    unitSpecifier?: string;
    /** Old price which is striked through */
    oldValue?: string | number;
    /** Additional small text to describe the price */
    description?: string | React.ReactNode;
    /** Size variants are sm, md, lg, xlg */
    size?: SupportedPriceSize;
    /** Default font is TeliaSansHeading but TeliaSans is allowed in some cases */
    font?: SupportedFont;
  },
  typeof Div
>;

const Price: React.FunctionComponent<PriceProps> = ({
  align = 'left',
  value,
  unit = '€',
  unitSpecifier,
  oldValue,
  description,
  prefix,
  size = 'md',
  font = 'teliasansHeading',
  ...props
}) => {
  const resolvedUnit = unitSpecifier ? `${unit}/${unitSpecifier}` : unit;

  return (
    <Div
      {...props}
      alignTextToRight={align === 'right'}
      alignTextToCenter={align === 'center'}
    >
      <Flex
        alignBottom
        alignRight={align === 'right'}
        centeredHorizontally={align === 'center'}
      >
        <Div>
          <Flex alignBaseline>
            {prefix && (
              <>
                <Typography
                  font={font}
                  fontWeight={font === 'teliasans' ? 'bold' : 'normal'}
                  uppercase
                  color="black"
                  className={styles[`price_${size}_prefix`]}
                >
                  {prefix}
                </Typography>

                {font === 'teliasans' && size === 'xlg' ? (
                  <Gutter className={styles.price_gutter} />
                ) : (
                  <Gutter size="xxs" />
                )}
              </>
            )}

            <Typography
              font={font}
              fontWeight={font === 'teliasans' ? 'bold' : 'normal'}
              uppercase
              color="black"
              className={styles[`price_${size}_value`]}
            >
              {value}
            </Typography>

            <Gutter size="xxs" />

            <Typography
              font={font}
              fontWeight={font === 'teliasans' ? 'bold' : 'normal'}
              color="black"
              uppercase
              className={styles[`price_${size}_unit`]}
            >
              {resolvedUnit}
            </Typography>

            {oldValue && (
              <>
                <Gutter className={styles.price_gutter} />

                <Typography
                  font={font}
                  fontWeight={font === 'teliasans' ? 'bold' : 'normal'}
                  uppercase
                  color="gray500"
                  strikeThrough
                  className={styles[`price_${size}_oldValue`]}
                >
                  {oldValue}
                </Typography>
              </>
            )}
          </Flex>
        </Div>
      </Flex>

      {typeof description === 'string' ? (
        <SmallText color="black">{description}</SmallText>
      ) : (
        description
      )}
    </Div>
  );
};

export const supportedPriceSizes = ['sm', 'md', 'lg', 'xlg'] as const;
export type SupportedPriceSize = ArrayElementType<typeof supportedPriceSizes>;

export default Price;
